//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useContext, useState } from 'react';
import axios from '@/_utils/axios';
import BulkSearchModal from '@/components/OrderPlacement/OPStep2VersionSelection/BulkSearch/BulkSearchModal/BulkSearchModal';
import OverflowMenu from '@/components/_common/OverflowMenu/OverflowMenu';
import OverflowMenuItem from '@/components/_common/OverflowMenu/OverflowMenuItem';
import { selectCanDeleteDraftOrders, selectCanWriteOrders } from '@/redux/selectors/authSelectors';
import { Order } from '@/redux/slices/slice_types';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { setDraftResumed, setDraftState } from '@/redux/slices/orderPlacementSlice';
import { useHistory } from 'react-router-dom';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import { getData } from '@/redux/slices/dataTableSlice';
import { IconButton, Tooltip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { convertOrderRequestsToDraftState } from '@/_utils/orderUtils';
import { selectAllProfiles } from '../../redux/selectors/profileSelectors';
import { ConfigContext } from '@/contextAPI/configContext';

type OrdersOverflowMenuProps = {
  order: Order;
};

const OrdersOverflowMenu = ({ order }: OrdersOverflowMenuProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const allProfiles = useAppSelector(selectAllProfiles);
  const { status } = order;
  const canDeleteDraftOrders = useAppSelector(selectCanDeleteDraftOrders);
  const canWriteOrders = useAppSelector(selectCanWriteOrders);
  const hasDraft = order.status === 'draft';
  const hideDelete = status !== 'draft' || !canDeleteDraftOrders;
  const isBulk = order.order_type === 'bulk';
  const customFields = useContext(ConfigContext)?.orderPlacementConfig?.addlFields?.orderDetails || {};

  const handleResumeDraft = (e: React.MouseEvent) => {
    e.stopPropagation();
    axios.get(`/core/orders/${order.id}/`).then((res) => {
      const draftState =
        res.data.data?.order_requests && Object.keys(res.data.data.order_requests).length > 0
          ? {
              ...convertOrderRequestsToDraftState(res.data.data, allProfiles),
              draftId: order.id
            }
          : {
              ...res.data.data.draft_state,
              draftId: order.id
            };

      // Add order-level data that might not be in the draft state or order requests
      if (draftState.values) {
        draftState.values = {
          ...draftState.values,
          description: res.data.data.description || draftState.values.description,
          po_number: res.data.data.po_number || draftState.values.po_number,
          // Keep these as strings for now - they'll be converted to objects in OrderPlacement
          billable_customer: res.data.data.billable_customer || draftState.values.billable_customer,
          managed_by: res.data.data.managed_by_user || draftState.values.managed_by_user
        };

        const customFieldKeys = Object.keys(customFields);

        if (customFieldKeys.length > 0) {
          customFieldKeys.forEach((fieldKey) => {
            if (res.data.data[fieldKey]) {
              draftState.values[fieldKey] = res.data.data[fieldKey];
            }
          });
        }
      }

      dispatch(setDraftState(draftState));
      dispatch(setDraftResumed(true));
      history.push('/orders-v2/new');
    });
  };

  const handleViewBulk = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    dispatch(getData());
  };

  const handleDelete = async () => {
    await axios({
      method: 'delete',
      baseURL: '/api/v2/core/',
      url: `/orders/${order.id}/`
    });
    dispatch(getData());
  };

  const { dialog: confirmationPrompt, setDialogVisible } = useConfirmationPrompt({
    title: 'Confirm Draft Delete',
    titleIcon: <PolarisIcon icon="cancel" height={19} style={{ opacity: 0.5, marginRight: 8 }} />,
    message: 'Are you sure you want to delete this draft order?',
    onConfirm: handleDelete,
    buttonColor: 'error',
    buttons: {
      submitText: 'Delete Draft',
      cancelText: 'No'
    }
  });

  let contents = [
    {
      name: 'draft',
      action: isBulk ? handleViewBulk : handleResumeDraft,
      iconOnly: (
        <Tooltip title={isBulk ? 'View Bulk Order' : 'Resume Draft'}>
          <IconButton onClick={isBulk ? handleViewBulk : handleResumeDraft} style={{ opacity: 0.6 }}>
            <PolarisIcon icon={isBulk ? 'bulk' : 'draft'} height={20} />
          </IconButton>
        </Tooltip>
      ),
      content: (
        <>
          <PolarisIcon icon={isBulk ? 'bulk' : 'draft'} height={20} />
          <span>{isBulk ? 'View Bulk Order' : 'Resume Draft'}</span>
        </>
      )
    }
  ];

  if (!isBulk && (!hasDraft || !canWriteOrders)) {
    contents = [];
  }

  if (!hideDelete) {
    contents.push({
      name: 'delete',
      action: () => setDialogVisible(true),
      iconOnly: (
        <Tooltip title="Delete Draft">
          <IconButton onClick={() => setDialogVisible(true)}>
            <PolarisIcon icon="cancel" height={18} width={18} style={{ opacity: 0.5 }} />
          </IconButton>
        </Tooltip>
      ),
      content: (
        <>
          <PolarisIcon icon="cancel" height={17} />
          <span>Delete Draft</span>
        </>
      )
    });
  }

  const modal = <BulkSearchModal open={isOpen} onClose={handleClose} id={order.id} />;

  if (contents.length <= 0) {
    return null;
  } else if (contents.length === 1) {
    return (
      <div onClick={contents[0].action}>
        {modal}
        {confirmationPrompt}
        {contents[0].iconOnly}
      </div>
    );
  }

  return (
    <>
      {modal}
      {confirmationPrompt}
      <OverflowMenu>
        {contents.map((item) => (
          <OverflowMenuItem key={item.name} onClick={item.action}>
            {item.content}
          </OverflowMenuItem>
        ))}
      </OverflowMenu>
    </>
  );
};

export default OrdersOverflowMenu;
